// hello─────────────────────────────────────────────
//  Profile.jsx  —  iOS 26 Glass Theme
//  Main controller — imports all tab components
//  Exports: Icons, StatCard, StatusBadge, DecisionBadge
//  All CSS classes prefixed with "Profile"
// ─────────────────────────────────────────────

import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./Profile.css";
import {
  useProfileState,
  APPLICATION_STATUS,
  DECISION_STATUS_LABELS,
  GENDER_OPTIONS,
  MARITAL_STATUS_OPTIONS,
  COUNTRY_CODES,
  emptyApplicant,
  TAB_LABELS,
  MAX_APPLICATIONS,
  INDIVIDUAL_LIMIT,
  WELCOME_BONUS,
  MIN_SVC_FEE_REDEMPTION,
  formatDate,
  formatCurrency,
  NAV_ITEMS_META,
  SIDEBAR_LINKS,
  TAB_TITLES,
  TAB_SUBS,
  calculateConvenienceFee,
  PAYMENT_METHODS,
  PAYMENT_METHOD_GROUPS,
  CONSENT_CLAUSES,
  CONSENT_DECLARATION_TEXT,
  checkRequiredDocsComplete,
} from "./ProfileLogic.js";

import UserService from '../../../services/userService';
import AuthService from '../../../services/authService';


// ── Tab Imports ────────────────────────────────
import OverviewTab      from "./ProfileOverview";
import ApplicationsTab  from "./ProfileApplication";
import RewardsTab       from "./ProfileRewards";
import DocumentsTab     from "./ProfileDocuments";
import SettingsTab      from "./ProfileSettings";
// ── Reusable fee info tooltip ─────────────────────────────────────
import { splitSvcFeeGST, PROTECT_FEE, PROTECT_FEE_FIRST, PROTECT_FEE_REPEAT } from "./ProfileLogic.js";
import { validatePaymentDetails, PAYMENT_DETAILS_DEFAULT, BANKS } from "../../Common/PaymentForm/PaymentForm";

function FeeInfoTooltip({ svcFee, side = "left" }) {
  const { base, gst } = splitSvcFeeGST(svcFee);
  return (
    <div className="ProfileFeeInfoWrap">
      <span className="ProfileFeeInfoIcon">i</span>
      <div className={`ProfileFeeTooltip ProfileFeeTooltip--${side}`}>
        <div className="ProfileFeeTooltipRow">
          <span>Base service fee</span>
          <span>₹{base.toLocaleString("en-IN")}</span>
        </div>
        <div className="ProfileFeeTooltipRow">
          <span>GST (18%)</span>
          <span>₹{gst.toLocaleString("en-IN")}</span>
        </div>
      </div>
    </div>
  );
}

function VisaProtectTooltip({ side = "left" }) {
  return (
    <div className="ProfileFeeInfoWrap">
      <span className="ProfileFeeInfoIcon">i</span>
      <div className={`ProfileFeeTooltip ProfileFeeTooltip--${side} ProfileFeeTooltip--visa-protect`}>
        <div className="ProfileFeeTooltipTitle">
          🛡️ Visa Protect Coverage
        </div>
      </div>
    </div>
  );
}

function ConvFeeInfoTooltip({ baseFee, gst, total, side = "left" }) {
  return (
    <div className="ProfileFeeInfoWrap">
      <span className="ProfileFeeInfoIcon">i</span>
      <div className={`ProfileFeeTooltip ProfileFeeTooltip--${side}`}>
        <div className="ProfileFeeTooltipRow">
          <span>Base convenience fee</span>
          <span>₹{baseFee.toLocaleString("en-IN")}</span>
        </div>
        <div className="ProfileFeeTooltipRow">
          <span>GST on conv. fee (18%)</span>
          <span>₹{gst.toLocaleString("en-IN")}</span>
        </div>
      </div>
    </div>
  );
}

// ── SVG Icons (exported for use in tab files) ──
export const Icons = {
  Overview: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  Applications: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
    </svg>
  ),
  Rewards: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
    </svg>
  ),
  Documents: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
    </svg>
  ),
  Settings: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.07 4.93l-1.41 1.41M4.93 4.93l1.41 1.41M4.93 19.07l1.41-1.41M19.07 19.07l-1.41-1.41M21 12h-2M5 12H3M12 21v-2M12 5V3"/>
    </svg>
  ),
  Logout: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  User: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
      <circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Check: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  Trash: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
      <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
    </svg>
  ),
  CreditCard: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
      <line x1="1" y1="10" x2="23" y2="10"/>
    </svg>
  ),
  Copy: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
      <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
    </svg>
  ),
  Globe: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
    </svg>
  ),
  Lock: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0110 0v4"/>
    </svg>
  ),
  Gift: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 12 20 22 4 22 4 12"/>
      <rect x="2" y="7" width="20" height="5"/>
      <line x1="12" y1="22" x2="12" y2="7"/>
      <path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z"/>
      <path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z"/>
    </svg>
  ),
  Upload: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 16 12 12 8 16"/>
      <line x1="12" y1="12" x2="12" y2="21"/>
      <path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/>
    </svg>
  ),
  Camera: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  Edit: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  ArrowRight: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
    </svg>
  ),
  ArrowLeft: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
    </svg>
  ),
  Close: () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Shield: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  ),
  Clock: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  Star: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
    </svg>
  ),
  Info: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
    </svg>
  ),
  TrendingUp: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
      <polyline points="17 6 23 6 23 12"/>
    </svg>
  ),
  Plus: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  Search: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  ),
  Download: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  Receipt: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l2-1.14"/>
      <path d="M16.5 9.4L7.55 4.24"/>
      <polyline points="3.29 7 12 12 20.71 7"/>
      <line x1="12" y1="22" x2="12" y2="12"/>
      <circle cx="18.5" cy="15.5" r="2.5"/>
      <path d="M20.27 17.27L22 19"/>
    </svg>
  ),
  Tag: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
      <line x1="7" y1="7" x2="7.01" y2="7"/>
    </svg>
  ),
  Bank: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="22" x2="21" y2="22"/>
      <line x1="6" y1="18" x2="6" y2="11"/>
      <line x1="10" y1="18" x2="10" y2="11"/>
      <line x1="14" y1="18" x2="14" y2="11"/>
      <line x1="18" y1="18" x2="18" y2="11"/>
      <polygon points="12 2 20 7 4 7"/>
    </svg>
  ),
  Mobile: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
      <line x1="12" y1="18" x2="12.01" y2="18"/>
    </svg>
  ),
  Filter: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
    </svg>
  ),
  Calendar: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  FileText: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  Trophy: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="8 21 12 17 16 21"/>
      <line x1="12" y1="17" x2="12" y2="11"/>
      <path d="M7 4H4a2 2 0 000 4c0 2.5 2 4.5 5 5"/>
      <path d="M17 4h3a2 2 0 010 4c0 2.5-2 4.5-5 5"/>
      <rect x="7" y="2" width="10" height="9" rx="1"/>
    </svg>
  ),
  XCircle: () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <line x1="15" y1="9" x2="9" y2="15"/>
      <line x1="9" y1="9" x2="15" y2="15"/>
    </svg>
  ),
  Zap: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
    </svg>
  ),
};

// ── NAV_ITEMS ──
const NAV_ITEMS = NAV_ITEMS_META.map((item) => ({
  ...item,
  Icon: Icons[item.key.charAt(0).toUpperCase() + item.key.slice(1)] ?? Icons.Overview,
}));

// ────────────────────────────────────────────
//  SHARED COMPONENTS
// ────────────────────────────────────────────

export function StatCard({ Icon, value, label, color }) {
  return (
    <div className="ProfileStatCard">
      <div className="ProfileStatIcon" style={{ color }}><Icon /></div>
      <div>
        <div className="ProfileStatValue">{value}</div>
        <div className="ProfileStatLabel">{label}</div>
      </div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    [APPLICATION_STATUS.PENDING]:   { cls: "ProfileStatusBadge--pending", label: "Pending"   },
    [APPLICATION_STATUS.COMPLETED]: { cls: "ProfileStatusBadge--done",    label: "Completed" },
  };
  const cfg = map[status] || map[APPLICATION_STATUS.PENDING];
  return (
    <span className={`ProfileStatusBadge ${cfg.cls}`}>
      <span className="ProfileStatusDot" />
      {cfg.label}
    </span>
  );
}

export function DecisionBadge({ decisionStatus }) {
  if (!decisionStatus) return null;
  const cfg = DECISION_STATUS_LABELS[decisionStatus];
  if (!cfg) return null;
  return (
    <span
      className="ProfileDecisionBadge"
      style={{ "--badge-color": cfg.color, "--badge-border": `${cfg.color}40`, "--badge-bg": `${cfg.color}10` }}
    >
      {cfg.icon} {cfg.label}
    </span>
  );
}

// ────────────────────────────────────────────
//  CAMERA MODAL
// ────────────────────────────────────────────
function CameraModal({ videoRef, onCapture, onClose }) {
  return (
    <div className="ProfileCameraModal">
      <p className="ProfileCameraPrompt">Position your document in the frame</p>
      <video ref={videoRef} autoPlay muted className="ProfileCameraVideo" />
      <div className="ProfileCameraControls">
        <button className="ProfileBtn ProfileBtnPrimary" onClick={onCapture}><Icons.Camera /> Capture</button>
        <button className="ProfileBtn ProfileBtnOutline ProfileBtnCameraCancel" onClick={onClose}>
          <Icons.Close /> Cancel
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  OTP SEPARATE BOXES INPUT
// ────────────────────────────────────────────
function OtpInputBoxes({ length = 6, value, onChange, disabled }) {
  const inputsRef = React.useRef([]);

  const handleChange = (index, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(0, 1);
    const newOtp = (value + '').split('');
    newOtp[index] = char;
    const joined = newOtp.join('').slice(0, length);
    onChange(joined);
    if (char && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      if (!value[index] && index > 0) {
        const newOtp = (value + '').split('');
        newOtp[index - 1] = '';
        onChange(newOtp.join(''));
        inputsRef.current[index - 1]?.focus();
      } else {
        const newOtp = (value + '').split('');
        newOtp[index] = '';
        onChange(newOtp.join(''));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    onChange(pasted);
    const focusIndex = Math.min(pasted.length, length - 1);
    inputsRef.current[focusIndex]?.focus();
  };

  const chars = (value + '').split('').concat(Array(length).fill('')).slice(0, length);

  return (
    <div className="ProfileOtpBoxes" onPaste={handlePaste}>
      {chars.map((char, i) => (
        <input
          key={i}
          ref={(el) => { inputsRef.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={char}
          disabled={disabled}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          className="ProfileOtpBox"
          autoComplete="one-time-code"
        />
      ))}
    </div>
  );
}

// ────────────────────────────────────────────
//  WELCOME PROFILE CARD
// ────────────────────────────────────────────
function WelcomeProfileCard({ user, onSave, onSkip }) {
  const fullUser = user;
  const nameParts = (fullUser?.name || '').split(' ');
  const [firstName, setFirstName] = useState(nameParts[0] || '');
  const [lastName, setLastName] = useState(nameParts.slice(1).join(' ') || '');
  const [email, setEmail] = useState(fullUser?.email || '');
  const [phone, setPhone] = useState(fullUser?.phone || '');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [saving, setSaving] = useState(false);

  const originalEmail = React.useRef(fullUser?.email || '');
  const originalPhone = React.useRef(fullUser?.phone || '');

  const [otpMethod, setOtpMethod] = useState(null);
  const [showMethodChoice, setShowMethodChoice] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [timer, setTimer] = useState(120);
  const [timerActive, setTimerActive] = useState(false);
  const timerRef = useRef(null);

  const [phoneCode, setPhoneCode] = useState('+91');

  const emailChanged = email !== originalEmail.current;
  const phoneChanged = phone !== originalPhone.current;

  useEffect(() => {
    if (fullUser?.phone && fullUser?.email) return;
    UserService.getProfile().then((p) => {
      if (p) {
        if (!originalEmail.current && p.email) { originalEmail.current = p.email; setEmail(p.email); }
        if (!originalPhone.current && p.phone) { originalPhone.current = p.phone; setPhone(p.phone); }
        if (!firstName && p.name) {
          const parts = p.name.split(' ');
          setFirstName(parts[0] || '');
          setLastName(parts.slice(1).join(' ') || '');
        }
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (emailChanged) { setEmailVerified(false); }
    if (phoneChanged) { setPhoneVerified(false); }
    if (emailChanged || phoneChanged) { setOtpSent(false); setOtp(''); setTimerActive(false); setTimer(120); setOtpMethod(null); }
  }, [email, phone]);

  useEffect(() => {
    if (timerActive && timer > 0) {
      timerRef.current = setTimeout(() => setTimer((t) => t - 1), 1000);
    } else if (timerActive && timer === 0) {
      setTimerActive(false);
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [timerActive, timer]);

  function formatTime(sec) {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  }

  const otpTargetEmail = originalEmail.current || email.trim();
  const otpTargetPhone = originalPhone.current || phone.trim();

  function handleSendOtp(method) {
    setOtpMethod(method);
    if (method === 'email') {
      if (!otpTargetEmail) { alert("No existing email to verify."); return; }
      setOtpSending(true);
      AuthService.sendIdentityEmailOtp().then(() => { setOtpSent(true); setOtp(''); setTimer(120); setTimerActive(true); }).catch((err) => { alert(err.response?.data?.message || "Failed to send OTP."); }).finally(() => setOtpSending(false));
    } else {
      if (!otpTargetPhone) { alert("No existing phone number to verify."); return; }
      setOtpSending(true);
      AuthService.sendPhoneOtp({ phone: otpTargetPhone, phoneCode, purpose: "verify_phone" }).then(() => { setOtpSent(true); setOtp(''); setTimer(120); setTimerActive(true); }).catch((err) => { alert(err.response?.data?.message || "Failed to send OTP."); }).finally(() => setOtpSending(false));
    }
  }

  function handleVerifyOtp() {
    if (!otp) { alert("Please enter the OTP."); return; }
    setOtpVerifying(true);
    const promise = otpMethod === 'email'
      ? AuthService.verifyOtp(otpTargetEmail, otp)
      : AuthService.verifyPhoneOtp({ phone: otpTargetPhone, phoneCode, otp, purpose: "verify_phone" });
    promise.then(() => {
      if (otpMethod === 'email') setEmailVerified(true);
      else setPhoneVerified(true);
      setTimerActive(false);
      setOtpSent(false);
      setOtp('');
      setOtpMethod(null);
      setShowMethodChoice(false);
    }).catch((err) => { alert(err.response?.data?.message || "OTP verification failed."); }).finally(() => setOtpVerifying(false));
  }

  const anyVerified = emailVerified || phoneVerified;
  const canSave = anyVerified && !!firstName.trim() && !!lastName.trim() && !!phone.trim();

  async function handleSave(e) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) { alert("Please enter your first and last name."); return; }
    if (!phone.trim()) { alert("Please enter your phone number."); return; }
    if (!canSave) { alert("Please verify via email or phone OTP first."); return; }
    setSaving(true);
    await onSave(firstName.trim(), lastName.trim(), phone.trim(), email.trim(), phoneCode, address.trim(), city.trim(), pinCode.trim());
    setSaving(false);
  }

  return (
    <div className="ProfileWelcomeOverlay">
      <div className="ProfileWelcomeModal">
        <div className="ProfileWelcomeHeader">
          <div className="ProfileWelcomeEmoji"><Icons.User /></div>
          <h2>Welcome, {firstName || 'there'}!</h2>
          <p>Please fill in your details to get started.</p>
        </div>
        <form className="ProfileWelcomeForm" onSubmit={handleSave}>
          <div className="ProfileWelcomeFormRow">
            <div className="ProfileWelcomeFormGroup">
              <label>First Name <span className="ProfileRequiredStar">*</span></label>
              <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First Name" required />
            </div>
            <div className="ProfileWelcomeFormGroup">
              <label>Last Name <span className="ProfileRequiredStar">*</span></label>
              <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last Name" required />
            </div>
          </div>
          <div className="ProfileWelcomeFormRow">
            <div className="ProfileWelcomeFormGroup">
              <label>Address</label>
              <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street address" />
            </div>
            <div className="ProfileWelcomeFormGroup">
              <label>Pin Code</label>
              <input type="text" value={pinCode} onChange={(e) => setPinCode(e.target.value.replace(/\D/g, ''))} placeholder="Pin code" maxLength={10} />
            </div>
          </div>
          <div className="ProfileWelcomeFormRow">
            <div className="ProfileWelcomeFormGroup">
              <label>Email {!originalEmail.current && <span className="ProfileRequiredStar">*</span>}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
            </div>
            <div className="ProfileWelcomeFormGroup">
              <label>Phone Number <span className="ProfileRequiredStar">*</span></label>
              <div className="ProfileWelcomeOtpRow">
                <select className="ProfileWelcomeCodeSelect" value={phoneCode} onChange={(e) => setPhoneCode(e.target.value)}>
                  {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                </select>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone Number" className="ProfileWelcomeOtpInput" />
            </div>
          </div>
          </div>
          {!(emailVerified && phoneVerified) && (
            <div className="ProfileWelcomeFormGroup">
              {!otpSent && (
                <div className="ProfileWelcomeOtpCenter">
                  <p style={{ fontSize: 13, color: 'var(--Profile-text-4)', marginTop: 12, marginBottom: 8 }}>Verify your identity — choose a method</p>
                  <div className="ProfileWelcomeOtpRow ProfileWelcomeOtpRow--center">
                    <button type="button" className="ProfileBtn ProfileBtnOutline ProfileBtnOtp" onClick={() => handleSendOtp('email')} disabled={otpSending || !otpTargetEmail}>
                      {otpSending && otpMethod === 'email' ? 'Sending...' : 'Email'}
                    </button>
                    <button type="button" className="ProfileBtn ProfileBtnOutline ProfileBtnOtp" onClick={() => handleSendOtp('phone')} disabled={otpSending || !otpTargetPhone}>
                      {otpSending && otpMethod === 'phone' ? 'Sending...' : 'Phone'}
                    </button>
                  </div>
                </div>
              )}
              {otpSent && (
                <div className="ProfileWelcomeOtpCenter" style={{ marginTop: 12 }}>
                  <p style={{ fontSize: 12, color: 'var(--Profile-text-4)', marginBottom: 4 }}>OTP sent to {otpMethod === 'email' ? otpTargetEmail : `${phoneCode} ${otpTargetPhone}`}</p>
                  <div className="ProfileWelcomeOtpRow ProfileWelcomeOtpRow--center">
                    <OtpInputBoxes length={6} value={otp} onChange={setOtp} disabled={otpVerifying} />
                    <button type="button" className="ProfileBtn ProfileBtnPrimary ProfileBtnOtp" onClick={handleVerifyOtp} disabled={otpVerifying}>
                      {otpVerifying ? 'Verifying...' : 'Verify'}
                    </button>
                    <button type="button" className="ProfileBtn ProfileWelcomeSkipBtn" onClick={() => { setOtpSent(false); setOtpMethod(null); setOtp(''); setTimerActive(false); setTimer(120); }}>
                      Cancel
                    </button>
                  </div>
                  {timerActive ? (
                    <div className="ProfileWelcomeTimer">Resend in {formatTime(timer)}</div>
                  ) : (
                    <button type="button" className="ProfileBtn ProfileBtnOutline ProfileBtnOtp" onClick={() => { setOtpSent(false); setOtp(''); setTimer(120); setTimerActive(true); handleSendOtp(otpMethod); }} style={{ marginTop: 4 }}>Resend</button>
                  )}
                </div>
              )}
            </div>
          )}
          {(emailVerified || phoneVerified) && (
            <div className="ProfileWelcomeOtpCenter" style={{ marginTop: 8 }}>
              <span className="ProfileWelcomeVerifiedBadge">
                {emailVerified && phoneVerified ? 'Email and Phone Verified' : emailVerified ? 'Email Verified' : 'Phone Verified'}
              </span>
            </div>
          )}
          <div className="ProfileWelcomeFormSubmit">
            {onSkip && (
              <button type="button" className="ProfileWelcomeSkipBtn" onClick={onSkip}>
                Skip now
              </button>
            )}
            <button className="ProfileBtn ProfileBtnPrimary" type="submit" disabled={saving || !canSave}>
              {saving ? <span className="ProfileSpinner" /> : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  WELCOME MODAL (document upload)
// ────────────────────────────────────────────
function WelcomeModal({ user, docRequirements, docFiles, uploadedDocs, uploading, onFilePick, onCamera, onSubmit, onSkip, onGoToDocuments }) {
  const { DocCard } = require("./ProfileDocuments");
  const requiredDocs = docRequirements.filter((d) => d.required);
  const allRequiredUploaded = requiredDocs.every((doc) => docFiles[doc.key] || uploadedDocs[doc.key]);
  const allAlreadySaved = requiredDocs.every((doc) => uploadedDocs[doc.key]);
  const hasNewFiles = Object.keys(docFiles).length > 0;
  
  const handleSaveSubmit = () => {
    if (!allRequiredUploaded) {
      alert("Please upload all required documents before saving.");
      return;
    }
    onSubmit();
  };
  
  const btnText = uploading ? <span className="ProfileSpinner" /> : (allAlreadySaved && !hasNewFiles) ? 'Uploaded' : 'Save';

  return (
    <div className="ProfileWelcomeOverlay">
      <div className="ProfileWelcomeModal">
        <div className="ProfileWelcomeHeader">
          <div className="ProfileWelcomeEmoji"><Icons.User /></div>
          <h2>Welcome, {user?.name?.split(" ")[0]}!</h2>
          <p>No more hunting for files,Store docs once apply smarter forever.</p>
        </div>
        <div className="ProfileDocsGrid">
          {docRequirements.map((doc) => (
            <DocCard 
              key={doc.key} 
              doc={doc} 
              uploaded={uploadedDocs[doc.key]} 
              staged={docFiles[doc.key]} 
              onFilePick={onFilePick} 
              onCamera={onCamera} 
              fileDataUrl={uploadedDocs[doc.key]?.url || null}
              uploadedName={uploadedDocs[doc.key]?.originalName || uploadedDocs[doc.key]?.original_name || null}
            />
          ))}
        </div>
        <div className="ProfileUploadSubmitBar">
          <div className="ProfileUploadActions">
            <button className="ProfileBtn ProfileWelcomeSkipBtn" onClick={onSkip}>
              Skip now
            </button>
            <button className="ProfileBtn ProfileBtnPrimary" onClick={handleSaveSubmit}>
              {btnText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  UPGRADE PROMPT MODAL
// ────────────────────────────────────────────
function UpgradePromptModal({ count, onDecision }) {
  return (
    <div className="ProfileUpgradeModal">
      <div className="ProfilePayModal ProfilePayModal--sm">
        <div className="ProfilePayModalHeader">
          <div className="ProfilePayModalTitle">
            {/* <div className="ProfilePayModalFlag"><Icons.Shield /></div> */}
            <div>
              <div className="ProfilePayModalCountry">Upgrade Recommended</div>
              <div className="ProfilePayModalType">Application Limit Exceeded</div>
            </div>
          </div>
        </div>
        <div className="ProfilePayModalBody ProfilePayModalBody--pt">
          <p className="ProfileUpgradeText">
            You are applying for <strong>{count} applications</strong>. Individuals are limited to <strong>{INDIVIDUAL_LIMIT}</strong>.
          </p>
          <p className="ProfileUpgradeTextSub">
            Upgrade to a <strong>Member</strong> account for higher limits and increased commission rates.
          </p>
          <div className="ProfileUpgradeBtns">
            <button className="ProfileBtn ProfileBtnOutline" onClick={() => onDecision(false)}>Continue </button>
            <button className="ProfileBtn ProfileBtnPrimary" onClick={() => onDecision(true)}>Upgrade</button>
          </div>
        </div>
      </div>
    </div>
  );
}





// ────────────────────────────────────────────
//  APPLICANT DOC UPLOAD (single document row)
// ────────────────────────────────────────────
function ApplicantDocUpload({ label, fieldKey, fileData, index, onFilePick, required }) {
  const inputRef = React.useRef(null);
  const hasFile  = !!(fileData?.file || fileData?._name);
  const fileName = fileData?.info?.name || fileData?._name || null;
  return (
    <div className="ProfileApplicantDocItem">
      <div className="ProfileApplicantDocLabel">
        {label}{required && <span className="ProfileRequiredStar">*</span>}
      </div>
      <div
        className={`ProfileApplicantDocBtn${hasFile ? " uploaded" : ""}`}
        onClick={() => inputRef.current?.click()}
      >
        {hasFile ? (
          <><Icons.Check /><span className="ProfileDocFileName">{fileName || "Uploaded"}</span></>
        ) : (
          <><Icons.Upload /><span className="ProfileDocFileLabel">Upload File</span></>
        )}
      </div>
      {fileData?._name && !fileData?.file && (
        <div className="ProfileDocReuploadNotice">Re-upload required</div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="ProfileHiddenInput"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFilePick(index, fieldKey, f); }}
      />
    </div>
  );
}

// ────────────────────────────────────────────
//  APPLICANT FORM BLOCK
// ────────────────────────────────────────────
function ApplicantFormBlock({ index, data, onUpdate, onFilePick, onRemove, onCamera, userRole }) {
  const [showOptional, setShowOptional] = useState(false);
  const updateField = (field, value) => onUpdate(index, field, value);

  return (
    <div className="ProfileApplicantBlock">
      <div className="ProfileApplicantBlockHeader">
        <h4>Applicant {index + 1}</h4>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span className="ProfileApplicantIdBadge">{data.passportNumber || ""}</span>
          {onRemove && (
            <button
              className="ProfileAppCardDeleteBtn"
              onClick={() => onRemove(index)}
              title="Remove applicant"
              style={{ fontSize: 13, marginTop: 0 }}
            >
              ✕
            </button>
          )}
        </div>
      </div>
      <div className="ProfilePayFormGrid">
        <div className="ProfilePfField"><label>First Name *</label><input value={data.firstName} onChange={(e) => updateField("firstName", e.target.value)} /></div>
        <div className="ProfilePfField"><label>Last Name *</label><input value={data.lastName} onChange={(e) => updateField("lastName", e.target.value)} /></div>
        <div className="ProfilePfField"><label>Date of Birth *</label><input type="date" value={data.dob} onChange={(e) => updateField("dob", e.target.value)} /></div>
        <div className="ProfilePfField">
          <label>Gender *</label>
          <select value={data.gender} onChange={(e) => updateField("gender", e.target.value)}>
            <option value="">Select</option>
            {GENDER_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="ProfilePfField">
          <label>Marital Status *</label>
          <select value={data.maritalStatus} onChange={(e) => updateField("maritalStatus", e.target.value)}>
            <option value="">Select</option>
            {MARITAL_STATUS_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="ProfilePfField"><label>Passport Number *</label><input value={data.passportNumber} onChange={(e) => updateField("passportNumber", e.target.value.toUpperCase())} /></div>
        <div className="ProfilePfField"><label>Valid Till *</label><input type="date" value={data.passportValidTill} onChange={(e) => updateField("passportValidTill", e.target.value)} /></div>
        <div className="ProfilePfField"><label>Place of Issue *</label><input value={data.placeOfIssue} onChange={(e) => updateField("placeOfIssue", e.target.value)} /></div>
        <div className="ProfilePfField"><label>Email *</label><input type="email" value={data.email} onChange={(e) => updateField("email", e.target.value)} /></div>
        <div className="ProfilePfField ProfilePfField--phone">
          <label>Phone *</label>
          <div className="ProfilePhoneRow">
            <select className="ProfilePhoneCodeSelect" value={data.phoneCode} onChange={(e) => updateField("phoneCode", e.target.value)}>
              {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
            </select>
            <input className="ProfilePhoneInput" value={data.phone} onChange={(e) => updateField("phone", e.target.value)} placeholder="9876543210" />
          </div>
        </div>
        <div className="ProfilePfField ProfilePfField--full"><label>Occupation *</label><input value={data.occupation} onChange={(e) => updateField("occupation", e.target.value)} /></div>
        {userRole === "agent" && (
          <div className="ProfilePfField">
            <label>Company / Agency Name</label>
            <input value={data.agencyName || ""} onChange={(e) => updateField("agencyName", e.target.value)} placeholder="e.g. Vision Travels Pvt. Ltd." />
          </div>
        )}
        <div className="ProfilePfField ProfilePfField--full">
          <button className="ProfilePfOptionalToggle" onClick={() => setShowOptional(!showOptional)}>
            {showOptional ? "▲" : "▼"} &nbsp; Optional Travel Details
          </button>
        </div>
      </div>
      {showOptional && (
        <div className="ProfilePfOptionalSection">
          <h5 className="ProfileOptionalSectionTitle">Flight Details</h5>
          <div className="ProfilePayFormGrid">
            <div className="ProfilePfField"><label>Flight Number</label><input value={data.flight.flightNumber} onChange={(e) => updateField("flight.flightNumber", e.target.value)} /></div>
            <div className="ProfilePfField"><label>Arrival Date</label><input type="date" value={data.flight.arrivalDate} onChange={(e) => updateField("flight.arrivalDate", e.target.value)} /></div>
            <div className="ProfilePfField"><label>Departure Date</label><input type="date" value={data.flight.departureDate} onChange={(e) => updateField("flight.departureDate", e.target.value)} /></div>
            <div className="ProfilePfField"><label>Stops</label><input value={data.flight.stops} onChange={(e) => updateField("flight.stops", e.target.value)} /></div>
          </div>
          <h5 className="ProfileOptionalSectionTitle ProfileOptionalSectionTitle--spaced">Transit Stops</h5>
          <div className="ProfilePayFormGrid">
            <div className="ProfilePfField"><label>Transit Flight #</label><input value={data.transit.flightNumber} onChange={(e) => updateField("transit.flightNumber", e.target.value)} /></div>
            <div className="ProfilePfField"><label>Arrival Date</label><input type="date" value={data.transit.arrivalDate} onChange={(e) => updateField("transit.arrivalDate", e.target.value)} /></div>
            <div className="ProfilePfField"><label>Departure Date</label><input type="date" value={data.transit.departureDate} onChange={(e) => updateField("transit.departureDate", e.target.value)} /></div>
            <div className="ProfilePfField"><label>Transit Stops</label><input value={data.transit.stops} onChange={(e) => updateField("transit.stops", e.target.value)} /></div>
          </div>
        </div>
      )}

      {/* Required Document Uploads */}
      <div className="ProfileApplicantDocsSection">
        <div className="ProfileApplicantDocsSectionTitle"><Icons.Documents /> Required Documents</div>
        <div className="ProfileApplicantDocsGrid">
          <ApplicantDocUpload label="Passport – First Page"     fieldKey="passportFront" fileData={data.passportFront} index={index} onFilePick={onFilePick} required />
          <ApplicantDocUpload label="Passport – Last Page"      fieldKey="passportBack"  fileData={data.passportBack}  index={index} onFilePick={onFilePick} required />
          <ApplicantDocUpload label="Bank Statement (3 months)" fieldKey="bankStatement" fileData={data.bankStatement} index={index} onFilePick={onFilePick} required />
          <ApplicantDocUpload label="Photo for Visa"            fieldKey="visaPhoto"     fileData={data.visaPhoto}    index={index} onFilePick={onFilePick} required />
        </div>
      </div>

      {/* Optional Document Uploads */}
      <div className="ProfileApplicantDocsSection">
        <div className="ProfileApplicantDocsSectionTitle ProfileApplicantDocsSectionTitle--optional"><Icons.Documents /> Optional Documents</div>
        <div className="ProfileApplicantDocsGrid">
          <ApplicantDocUpload label="Biometric Receipt"     fieldKey="biometric"        fileData={data.biometric}        index={index} onFilePick={onFilePick} />
          <ApplicantDocUpload label="Flight Ticket"         fieldKey="flightTicket"     fileData={data.flightTicket}     index={index} onFilePick={onFilePick} />
          <ApplicantDocUpload label="Travel Insurance"      fieldKey="travelInsurance"  fileData={data.travelInsurance}  index={index} onFilePick={onFilePick} />
          <ApplicantDocUpload label="Invitation Letter"     fieldKey="invitationLetter" fileData={data.invitationLetter} index={index} onFilePick={onFilePick} />
          <ApplicantDocUpload label="Other Document"        fieldKey="otherDoc"         fileData={data.otherDoc}         index={index} onFilePick={onFilePick} />
          <ApplicantDocUpload label="Other Document 2"      fieldKey="otherDoc2"        fileData={data.otherDoc2}        index={index} onFilePick={onFilePick} />
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  PAYMENT MODAL
// ────────────────────────────────────────────
function PaymentModal({
  app, payStep, setPayStep,
  applicantList, user, updateApplicantField, handleApplicantFilePick, removeApplicant, addApplicant,
  appCountInput, setAppCountInput, confirmProceedWithCount,
  showUpgradeModal, handleUpgradeDecision,
  referralInput, setReferralInput, referralError,
  referralApplied, discountAmount, applyReferralCode,
  payProcessing, handlePayNow, onClose, saveCurrentDraft,
  walletBalance, useWalletOffer, setUseWalletOffer,
  selectedPaymentMethod, setSelectedPaymentMethod,
  userRole,
  hasPaid,
}) {
  const [authorisedName, setAuthorisedName] = useState("");
  const [authorisedId, setAuthorisedId] = useState("");
  const [authCountryCode, setAuthCountryCode] = useState("+91");
  const [authPhone, setAuthPhone] = useState("");
  const [authOtp, setAuthOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [generatedOtp, setGeneratedOtp] = useState("");

  const sendOtp = () => {
    const fullPhone = `${authCountryCode} ${authPhone}`.trim();
    if (!authPhone.trim() || authPhone.replace(/\D/g, "").length < 10) {
      alert("Please enter a valid 10-digit phone number.");
      return;
    }
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    setGeneratedOtp(otp);
    setOtpSent(true);
    setOtpVerified(false);
    setAuthOtp("");
    setOtpTimer(60);
    console.log(`[OTP Simulation] Phone: ${fullPhone}, OTP: ${otp}`);
    const interval = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const verifyOtp = () => {
    if (!authOtp.trim()) {
      alert("Please enter the OTP.");
      return;
    }
    if (authOtp === generatedOtp) {
      setOtpVerified(true);
    } else {
      alert("Invalid OTP. Please try again.");
      setAuthOtp("");
    }
  };

  const resendOtp = () => {
    if (otpTimer > 0) return;
    sendOtp();
  };

  const handleStepTransition = (nextStep) => {
    setPayStep(nextStep);
  };

  if (payStep === 1) {
    return (
      <>
        <div className="ProfilePayOverlay">
          <div className="ProfilePayModal">
            <div className="ProfilePayModalHeader">
              <div className="ProfilePayModalTitle">
                {/* <div className="ProfilePayModalFlag"><Icons.Globe /></div> */}
                <div>
                  <div className="ProfilePayModalCountry">{app.countryName}</div>
                  <div className="ProfilePayModalType">{app.visaTypeLabel}</div>
                </div>
              </div>
              <button className="ProfilePayModalClose" onClick={onClose}><Icons.Close /></button>
            </div>
            <div className="ProfilePayModalBody">
              
              <div className="ProfilePayCountSection">
                <label className="ProfilePayCountLabel">How many applicants are you applying for?</label>
                <div className="ProfilePayCountRow">
                  <button className="ProfilePayCountBtn" onClick={() => setAppCountInput(Math.max(1, appCountInput - 1))}>−</button>
                  <span className="ProfilePayCountNum">{appCountInput}</span>
                  <button className="ProfilePayCountBtn" onClick={() => setAppCountInput(appCountInput + 1)}>+</button>
                </div>
              </div>
            </div>
            <div className="ProfilePayModalFooter">
              <button className="ProfileBtnPay" onClick={() => {
                if (userRole === "agent") { handleStepTransition("agent_auth"); } else { confirmProceedWithCount(); }
              }}>Continue </button>
            </div>
          </div>
        </div>
        {showUpgradeModal && <UpgradePromptModal count={appCountInput} onDecision={handleUpgradeDecision} />}
      </>
    );
  }

  if (payStep === "agent_auth") {
    return (
      <div className="ProfilePayOverlay">
        <div className="ProfilePayModal">
          <div className="ProfilePayModalHeader">
            <div className="ProfilePayModalTitle">
              {/* <div className="ProfilePayModalFlag"><Icons.Shield /></div> */}
              <div>
                <div className="ProfilePayModalCountry">{app.countryName}</div>
                <div className="ProfilePayModalType">{app.visaTypeLabel}</div>
              </div>
            </div>
            <button className="ProfilePayModalClose" onClick={onClose}><Icons.Close /></button>
          </div>
          <div className="ProfilePayModalBody">
            <div className="ProfilePayStepTitle">Who is filing on behalf of the company?</div>
            <div className="ProfilePayStepSub">These details will be saved with the application and printed on the receipt.</div>
            <div className="ProfileAgentAuthSection">
              <div className="ProfileAgentAuthFields">
                <div className="ProfilePfField">
                  <label>Full Name of Authorised Person *</label>
                  <input value={authorisedName} onChange={(e) => setAuthorisedName(e.target.value)} placeholder="e.g. Rahul Sharma" autoFocus />
                </div>
                <div className="ProfilePfField">
                  <label>Authorised ID / Employee ID *</label>
                  <input value={authorisedId} onChange={(e) => setAuthorisedId(e.target.value)} placeholder="e.g. EMP-001 or National ID" />
                </div>
                <div className="ProfilePfField ProfilePfField--phone">
                  <label>Phone Number *</label>
                  <div className="ProfilePhoneOtpRow">
                    <div className="ProfilePhoneRow" style={{ flex: 1 }}>
                      <select className="ProfilePhoneCodeSelect" value={authCountryCode} onChange={(e) => { setAuthCountryCode(e.target.value); setOtpVerified(false); }}>
                        {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.flag} {c.code}</option>)}
                      </select>
                      <input className="ProfilePhoneInput" value={authPhone} onChange={(e) => { const v = e.target.value.replace(/[^\d+\-\s()]/g, ""); setAuthPhone(v); setOtpVerified(false); }} placeholder="9876543210" maxLength={15} disabled={otpSent} />
                    </div>
                    {!otpSent ? (
                      <button className="ProfileOtpSendBtn" onClick={sendOtp} disabled={!authPhone.trim()}>Send OTP</button>
                    ) : (
                      <span className="ProfileOtpSentBadge">OTP Sent</span>
                    )}
                  </div>
                </div>
                {otpSent && (
                  <div className="ProfileOtpNotice">
                    <span>Your OTP is:</span>
                    <span className="ProfileOtpCode">{generatedOtp}</span>
                  </div>
                )}
                {otpSent && (
                  <div className="ProfilePfField">
                    <label>Enter OTP *</label>
                    <div className="ProfilePhoneOtpRow">
                      <input className="ProfilePhoneOtpInput ProfileOtpInput" value={authOtp} onChange={(e) => { setAuthOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setOtpVerified(false); }} placeholder="6-digit OTP" maxLength={6} inputMode="numeric" disabled={otpVerified} />
                      {otpVerified ? (
                        <span className="ProfileOtpVerifiedBadge">Verified</span>
                      ) : (
                        <button className="ProfileOtpVerifyBtn" onClick={verifyOtp} disabled={!authOtp.trim() || authOtp.length < 6}>Verify</button>
                      )}
                    </div>
                    <div className="ProfileOtpFooter">
                      {otpTimer > 0 ? (
                        <span>Resend in <strong>{otpTimer}s</strong></span>
                      ) : (
                        <button className="ProfileOtpResendBtn" onClick={resendOtp}>Resend</button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
            <div className="ProfilePayModalFooter">
              <button className="ProfileBtn ProfileBtnOutline" onClick={() => { saveCurrentDraft(); setPayStep(1); }}> Back</button>
              <button className="ProfileBtnPay" onClick={() => {
                if (!authorisedName.trim() || !authorisedId.trim()) {
                  alert("Please fill in both the Authorised Person Name and ID.");
                  return;
                }
                if (!otpVerified) {
                  alert("Please verify your phone number with OTP before continuing.");
                  return;
                }
                confirmProceedWithCount();
              }}>Continue </button>
            </div>
</div>
    </div>
  );
}

if (payStep === 2) {
    return (
      <PaymentModalStep2
        applicantList={applicantList}
        updateApplicantField={updateApplicantField}
        handleApplicantFilePick={handleApplicantFilePick}
        removeApplicant={removeApplicant}
        addApplicant={addApplicant}
        onNext={() => handleStepTransition(3)}
        onBack={() => { saveCurrentDraft(); setPayStep(1); }}
        userRole={userRole}
        onClose={onClose}
      />
    );
  }

  if (payStep === 3) {
    return (
      <PaymentModalStep4
        app={app} applicantList={applicantList} user={user}
        onNext={() => handleStepTransition(4)}
        onBack={() => { saveCurrentDraft(); setPayStep(2); }} onClose={onClose}
        userRole={userRole}
      />
    );
  }

  return (
    <PaymentModalStep3
      app={app} applicantList={applicantList} user={user}
      referralInput={referralInput} setReferralInput={setReferralInput}
      referralError={referralError}
      referralApplied={referralApplied} discountAmount={discountAmount}
      applyReferralCode={applyReferralCode}
      onNext={() => handleStepTransition(4)}
      onBack={() => { saveCurrentDraft(); setPayStep(3); }} onClose={onClose}
      walletBalance={walletBalance}
      useWalletOffer={useWalletOffer} setUseWalletOffer={setUseWalletOffer}
      selectedPaymentMethod={selectedPaymentMethod}
      setSelectedPaymentMethod={setSelectedPaymentMethod}
      userRole={userRole}
      payProcessing={payProcessing}
      handlePayNow={(selectedPaymentMethod, paymentDetails) => handlePayNow(selectedPaymentMethod, paymentDetails, { authorisedName, authorisedId, authorisedPhone: authPhone, authorisedCountryCode: authCountryCode })}
      authorisedName={authorisedName}
      authorisedId={authorisedId}
      authorisedPhone={authPhone}
      authorisedCountryCode={authCountryCode}
      hasPaid={hasPaid}
    />
  );
}

function PaymentModalStep2({ applicantList, updateApplicantField, handleApplicantFilePick, removeApplicant, addApplicant, onNext, onBack, onClose, userRole }) {
  function validateDocs() {
    const DOC_FIELDS = ["passportFront", "passportBack", "bankStatement", "visaPhoto"];
    const DOC_LABELS = { passportFront: "Passport First Page", passportBack: "Passport Last Page", bankStatement: "Bank Statement", visaPhoto: "Photo for Visa" };
    for (let i = 0; i < applicantList.length; i++) {
      const a = applicantList[i];
      for (const f of DOC_FIELDS) {
        if (!a[f]?.file && !a[f]?._name) {
          return { valid: false, message: `Applicant ${i + 1}: Please upload "${DOC_LABELS[f]}".` };
        }
      }
    }
    return { valid: true };
  }
  return (
    <div className="ProfilePayOverlay">
      <div className="ProfilePayModal ProfilePayModal--wide">
        <div className="ProfilePayModalHeader">
          <div className="ProfilePayModalTitle">
            {/* <div className="ProfilePayModalFlag"><Icons.Documents /></div> */}
            <div>
              <div className="ProfilePayModalCountry">Applicant Details</div>
              <div className="ProfilePayModalType">{applicantList.length} Applications</div>
            </div>
          </div>
          <button className="ProfilePayModalClose" onClick={onClose}><Icons.Close /></button>
        </div>
        <div className="ProfilePayModalBody ProfilePayFormBody">
          
          {applicantList.map((applicant, idx) => (
            <ApplicantFormBlock key={idx} index={idx} data={applicant} onUpdate={updateApplicantField} onFilePick={handleApplicantFilePick} onRemove={removeApplicant} userRole={userRole} />
          ))}
          <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
            <button className="ProfileBtn ProfileBtnOutline" onClick={addApplicant}>+ Add Applicant</button>
          </div>
        </div>
        <div className="ProfilePayModalFooter">
          <button className="ProfileBtn ProfileBtnOutline" onClick={onBack}> Back</button>
          <button className="ProfileBtn ProfileBtnPrimary" onClick={() => {
            const r = validateDocs();
            if (!r.valid) { alert(r.message); return; }
            onNext();
          }}>Save and pay </button>
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────
//  MAIN PROFILE PAGE
// ────────────────────────────────────────────
export default function Profile({ onLogout }) {
  const navigate = useNavigate();

  function handleLogoutAndRedirect() {
    if (onLogout) onLogout();
    navigate("/");
  }

  const {
    user, setUser,
    showWelcome, dismissWelcome,
    editMode, setEditMode,
    activeTab, setActiveTab,
    uploading, saveSuccess,
    cameraActive, videoRef,
    docFiles, uploadedDocs,
    editName, setEditName,
    editPhone, setEditPhone,
    editCity, setEditCity,
    docRequirements, roleConfig,
    commissionRate, commissionPct,
    handleFilePick,
    openCamera, capturePhoto, closeCamera,
    handleUploadDocuments,
    handleSaveProfile,
    handleLogout,
    applications, pendingApplications, completedApplications, processingApplications, decisionApplications, handleSetDecision,
    handleDeleteApplication,
    wallet, nonRedeemableBalance, handleRedeemPoints,
    referralCode,
    milestoneData,
    paymentModal, openPaymentModal, closePaymentModal, saveCurrentDraft,
    payStep, setPayStep,
    applicantList, updateApplicantField, handleApplicantFilePick, removeApplicant, addApplicant,
    appCountInput, setAppCountInput, confirmProceedWithCount,
    showUpgradeModal, handleUpgradeDecision,
    referralInput, setReferralInput,
    referralError, setReferralError,
    referralApplied, discountAmount, applyReferralCode,
    useWalletOffer, setUseWalletOffer,
    selectedPaymentMethod, setSelectedPaymentMethod,
    payProcessing, handlePayNow,
    redemptionRequests,
    voucherRedemptions,
    payoutRequests,
    handleSubmitBillRequest,
    handleVoucherRedemptionRequest,
    handlePayoutRequest,
    draftMap,
    hasPaid,
  } = useProfileState(handleLogoutAndRedirect);

  const [appFilter, setAppFilter] = useState("pending");
  const [profileCardDone, setProfileCardDone] = useState(false);
  const showProfileCard = !!user && !profileCardDone;

  async function handleProfileCardSave(firstName, lastName, phone, email, phoneCode, address, city, pinCode) {
    try {
      const payload = { name: `${firstName} ${lastName}`.trim(), phone, email, phoneCode, address, city, pinCode };
      await UserService.updateProfile(payload);
      const fullName = `${firstName} ${lastName}`.trim();
      setUser((prev) => ({ ...prev, name: fullName, phone, email, phoneCode, address, city, pinCode }));
      setEditName(fullName);
      setEditPhone(phone);
    } catch { /* fallback */ }
    setProfileCardDone(true);
  }

  if (!user) {
    return (
      <div className="ProfilePage" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <p style={{ color: "var(--Profile-text-4)" }}>Not logged in.</p>
      </div>
    );
  }

  const tabSubResolved = {
    ...TAB_SUBS,
    overview: `Welcome back, ${user.name?.split(" ")[0]}`,
  };

  const walletBalance = nonRedeemableBalance;

  return (
    <div className="ProfilePage">
      {/* ── Modals ── */}

      {/* Welcome profile card (name, email, phone) */}
      {showProfileCard && (
        <WelcomeProfileCard
          user={user}
          onSave={handleProfileCardSave}
          onSkip={() => { setProfileCardDone(true); }}

        />
      )}

      {/* Welcome / onboarding docs modal */}
      {showWelcome && !showProfileCard && (
        <WelcomeModal
          user={user}
          docRequirements={docRequirements}
          docFiles={docFiles}
          uploadedDocs={uploadedDocs}
          uploading={uploading}
          onFilePick={handleFilePick}
          onCamera={openCamera}
          onSubmit={handleUploadDocuments}
          onSkip={() => dismissWelcome(false)}
          onGoToDocuments={() => dismissWelcome(true)}
        />
      )}

      {/* Camera modal */}
      {cameraActive && (
        <CameraModal videoRef={videoRef} onCapture={capturePhoto} onClose={closeCamera} />
      )}

      {/* Payment modal */}
      {paymentModal && (
        <PaymentModal
          app={paymentModal}
          payStep={payStep} setPayStep={setPayStep}
          applicantList={applicantList}
          user={user}
          updateApplicantField={updateApplicantField}
          handleApplicantFilePick={handleApplicantFilePick}
          removeApplicant={removeApplicant}
          addApplicant={addApplicant}
          appCountInput={appCountInput} setAppCountInput={setAppCountInput}
          confirmProceedWithCount={confirmProceedWithCount}
          showUpgradeModal={showUpgradeModal}
          handleUpgradeDecision={handleUpgradeDecision}
          referralInput={referralInput} setReferralInput={setReferralInput}
          referralError={referralError}
          referralApplied={referralApplied} discountAmount={discountAmount}
          applyReferralCode={applyReferralCode}
          payProcessing={payProcessing} handlePayNow={handlePayNow}
          onClose={closePaymentModal}
          saveCurrentDraft={saveCurrentDraft}
          walletBalance={nonRedeemableBalance}
          useWalletOffer={useWalletOffer}
          setUseWalletOffer={setUseWalletOffer}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          userRole={user?.role}
          hasPaid={hasPaid}
        />
      )}

      {saveSuccess && (
        <div className="ProfileSuccessToast">
          <Icons.Check /> Changes saved successfully
        </div>
      )}

      {/* ── Layout ── */}
      <div className="ProfileLayout">
        {/* SIDEBAR */}
        <aside className="ProfileSidebar">
          <div className="ProfileAvatarBlock">
            <div
              className="ProfileAvatarRole"
              style={{ background: `${roleConfig.color}14`, color: roleConfig.color, borderColor: `${roleConfig.color}28`, border: "1px solid" }}
            >
              {roleConfig.label}
            </div>
            <div className="ProfileAvatarName">{user.name}</div>
            {(user?.human_id || user?.userId) && (
              <div className="ProfileAvatarUserId">
                {/* <span className="ProfileAvatarUserIdLabel">ID</span> */}
                <span className="ProfileAvatarUserIdValue">{user?.human_id || user?.userId}</span>
              </div>
            )}
            {user.companyName && user.role === "agent" && (
              <div className="ProfileAvatarCompany">{user.companyName}</div>
            )}
          </div>

          {NAV_ITEMS.map(({ key, Icon, label }) => (
            <div key={key} className={`ProfileNavItem ${activeTab === key ? "active" : ""}`} onClick={() => setActiveTab(key)}>
              <span className="ProfileNavIcon"><Icon /></span>
              {label}
              {key === "applications" && applications.length > 0 && (
                <span className="ProfileNavBadge">{applications.length}</span>
              )}
              {key === "rewards" && (wallet?.redeemable?.balance ?? 0) > 0 && (
                <span className="ProfileNavBadge ProfileNavBadge--gold">
                  ₹{wallet.redeemable.balance.toLocaleString("en-IN")}
                </span>
              )}
            </div>
          ))}

<div className="ProfileSidebarDivider" />
          {SIDEBAR_LINKS.map(({ key, label, icon, path }) => (
            <div key={key} className="ProfileNavItem" onClick={() => navigate(path)} title={label}>
              <span className="ProfileNavIcon">{icon}</span>
              <span className="ProfileNavLabel">{label}</span>
            </div>
          ))}
<div className="ProfileSidebarDivider" />
          <div className="ProfileSidebarLogout" onClick={handleLogout}>
            <span className="ProfileNavIcon"><Icons.Logout /></span>
            Sign Out
          </div>
        </aside>

        <main className="ProfileContent">
          <div className="ProfileSectionHeader" data-aos="fade-up">
            <h2>{TAB_TITLES[activeTab]}</h2>
            <p>{tabSubResolved[activeTab]}</p>
          </div>

          {activeTab === "overview" && (
            <OverviewTab
              user={user}
              roleConfig={roleConfig}
              editMode={editMode}
              editName={editName} editPhone={editPhone} editCity={editCity}
              setEditName={setEditName} setEditPhone={setEditPhone} setEditCity={setEditCity}
              onEdit={(v) => setEditMode(v !== false)}
              onSave={handleSaveProfile}
              uploading={uploading}
              pendingApplications={pendingApplications}
              completedApplications={completedApplications}
              processingApplications={processingApplications}
              decisionApplications={decisionApplications}
              applications={applications}
              onPay={openPaymentModal}
              onDelete={handleDeleteApplication}
              onSetDecision={handleSetDecision}
              onViewAllApplications={(f) => { setAppFilter(f || "pending"); setActiveTab("applications"); }}
              wallet={wallet}
              commissionPct={commissionPct}
              referralCode={referralCode}
              draftMap={draftMap}
              uploadedDocs={uploadedDocs}
              docFiles={docFiles}
              onGoToDocuments={() => setActiveTab("documents")}
            />
          )}

          {activeTab === "applications" && (
            <ApplicationsTab
              applications={applications}
              pendingApplications={pendingApplications}
              completedApplications={completedApplications}
              processingApplications={processingApplications}
              decisionApplications={decisionApplications}
              onPay={openPaymentModal}
              onDelete={handleDeleteApplication}
              onSetDecision={handleSetDecision}
              draftMap={draftMap}
              profileComplete={user?.profileComplete}
              onGoToDocuments={() => setActiveTab("documents")}
              initialFilter={appFilter}
              userEmail={user?.email}
              userRole={user?.role}
            />
          )}

          {activeTab === "rewards" && (
            <RewardsTab
              wallet={wallet}
              user={user}
              commissionPct={commissionPct}
              referralCode={referralCode}
              milestoneData={milestoneData}
              redemptionRequests={redemptionRequests}
              voucherRedemptions={voucherRedemptions}
              payoutRequests={payoutRequests}
              onSubmitBill={handleSubmitBillRequest}
              onVoucherRedeem={handleVoucherRedemptionRequest}
              onPayoutRequest={handlePayoutRequest}
            />
          )}

          {activeTab === "documents" && (
            <DocumentsTab
              docRequirements={docRequirements}
              docFiles={docFiles}
              uploadedDocs={uploadedDocs}
              uploading={uploading}
              onFilePick={handleFilePick}
              onCamera={openCamera}
              onSubmit={handleUploadDocuments}
              applications={applications}
              userEmail={user?.email}
            />
          )}

          {activeTab === "settings" && (
            <SettingsTab
              user={user}
              onLogout={handleLogout}
              onSave={async (updates) => {
                await UserService.updateProfile(updates);
                setUser((prev) => ({ ...prev, ...updates }));
              }}
            />
          )}
        </main>
      </div>

      {/* Mobile Bottom Nav */}
      <nav className="ProfileMobileNav">
        {NAV_ITEMS.map(({ key, Icon, label }, idx) => (
          <div 
            key={key} 
            className={`ProfileMobileNavItem ${activeTab === key ? "active" : ""}`} 
            onClick={() => setActiveTab(key)}
            data-aos="fade-up"
            data-aos-delay={idx * 50}
          >
            <span className="ProfileMobileNavIcon"><Icon /></span>
            {label}
          </div>
        ))}
      </nav>
    </div>
  );
}

// ── Network Logo SVGs ────────────────────────────────────────────────────────
function NetworkLogo({ network, size = 28 }) {
  const s = size;
  if (network === "visa") return (
    <svg width={s} height={s * 0.63} viewBox="0 0 48 30" fill="none">
      <rect width="48" height="30" rx="4" fill="#1434CB"/>
      <text x="6" y="22" fontFamily="Arial,sans-serif" fontSize="14" fontWeight="700" fill="#fff" letterSpacing="1">VISA</text>
    </svg>
  );
  if (network === "mastercard") return (
    <svg width={s} height={s * 0.63} viewBox="0 0 48 30" fill="none">
      <circle cx="17" cy="15" r="10" fill="#EB001B"/>
      <circle cx="31" cy="15" r="10" fill="#F79E1B"/>
      <path d="M24 7.5a10 10 0 0 1 0 15 10 10 0 0 1 0-15z" fill="#FF5F00"/>
    </svg>
  );
  if (network === "rupay") return (
    <svg width={s} height={s * 0.63} viewBox="0 0 48 30" fill="none">
      <rect width="48" height="30" rx="4" fill="#007DC6"/>
      <text x="5" y="21" fontFamily="Arial,sans-serif" fontSize="12" fontWeight="800" fill="#fff">RuPay</text>
    </svg>
  );
  if (network === "upi") return (
    <svg width={s} height={s} viewBox="0 0 48 48" fill="none">
      <rect width="48" height="48" rx="8" fill="#6739B7"/>
      <text x="24" y="30" fontFamily="Arial,sans-serif" fontSize="16" fontWeight="800" fill="#fff" textAnchor="middle">UPI</text>
    </svg>
  );
}

// ── Individual Method Radio Row ──────────────────────────────────────────────
function PaymentMethodRow({ method, selected, onSelect, baseAmount }) {
  const isSelected = selected === method.key;
  const fees = calculateConvenienceFee(baseAmount, method.key);
  const hasFee = fees.totalFee > 0;

  return (
    <div
      className={`ProfilePayMethodRow ${isSelected ? "selected" : ""}`}
      onClick={() => onSelect(method.key)}
    >
      <div className="ProfilePayMethodRadio">
        <span className={`ProfilePayMethodDot ${isSelected ? "active" : ""}`} />
      </div>
      <div className="ProfilePayMethodNetworkLogo">
        <NetworkLogo network={method.icon} size={26} />
      </div>
      <div className="ProfilePayMethodInfo">
        <div className="ProfilePayMethodLabel">{method.shortLabel}</div>
        <div className="ProfilePayMethodDesc">{method.description}</div>
        {method.note && isSelected && (
          <div className="ProfilePayMethodNote">{method.note}</div>
        )}
      </div>
      <div className="ProfilePayMethodFeeTag">
        {hasFee ? (
          <span className="ProfilePayMethodFeeAmt">+₹{fees.totalFee.toLocaleString("en-IN")}</span>
        ) : (
          <span className="ProfilePayMethodFeeFree">Free</span>
        )}
        <div className="ProfilePayMethodTime">{method.processingTime}</div>
      </div>
    </div>
  );
}

// ── Expandable Group ─────────────────────────────────────────────────────────
function PaymentMethodGroup({ group, selected, onSelect, baseAmount, defaultOpen }) {
  const [open, setOpen] = React.useState(defaultOpen || false);
  const isGroupSelected = group.methods.some((m) => m.key === selected);

  return (
    <div className={`ProfilePayMethodGroup ${isGroupSelected ? "group-selected" : ""}`}>
      <div className="ProfilePayMethodGroupHeader" onClick={() => setOpen((v) => !v)}>
        <div className="ProfilePayMethodGroupLeft">
          <div className="ProfilePayMethodGroupIcon">
            {group.icon === "card" ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
            ) : group.icon === "mobile" || group.icon === "upi" ? (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                <line x1="12" y1="18" x2="12.01" y2="18"/>
              </svg>
            ) : (
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="22" x2="21" y2="22"/>
                <line x1="6" y1="18" x2="6" y2="11"/>
                <line x1="10" y1="18" x2="10" y2="11"/>
                <line x1="14" y1="18" x2="14" y2="11"/>
                <line x1="18" y1="18" x2="18" y2="11"/>
                <polygon points="12 2 20 7 4 7"/>
              </svg>
            )}
          </div>
          <div>
            <div className="ProfilePayMethodGroupLabel">{group.groupLabel}</div>
            <div className="ProfilePayMethodGroupDesc">{group.description}</div>
          </div>
        </div>
        <div className="ProfilePayMethodGroupRight">
          {isGroupSelected && <span className="ProfilePayMethodGroupActiveBadge">Selected</span>}
          <span className="ProfilePayMethodGroupChevron">{open ? "▲" : "▼"}</span>
        </div>
      </div>
      {open && (
        <div className="ProfilePayMethodGroupBody">
          {group.methods.map((method) => (
            <PaymentMethodRow
              key={method.key}
              method={method}
              selected={selected}
              onSelect={onSelect}
              baseAmount={baseAmount}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main Payment Method Selector ─────────────────────────────────────────────
export function PaymentMethodSelector({ selectedMethod, onSelect, baseAmount }) {
  return (
    <div className="ProfilePayMethodSelector">
      <div className="ProfilePaySummaryLabel" style={{ marginBottom: 12 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
        </svg>
        Payment Method
      </div>
      {PAYMENT_METHOD_GROUPS.map((group, idx) => (
        <PaymentMethodGroup
          key={group.groupKey}
          group={group}
          selected={selectedMethod}
          onSelect={onSelect}
          baseAmount={baseAmount}
          defaultOpen={idx === 0}
        />
      ))}
    </div>
  );
}

// ── PaymentModalStep3 ────────────────────────────────────────────────────────
function PaymentModalStep3({
  app, applicantList, user,
  referralInput, setReferralInput,
  referralError, referralApplied, discountAmount,
  applyReferralCode, onNext, onBack, onClose,
  walletBalance, useWalletOffer, setUseWalletOffer,
  selectedPaymentMethod, setSelectedPaymentMethod,
  userRole, payProcessing, handlePayNow,
  authorisedName, authorisedId, authorisedPhone, authorisedCountryCode,
  hasPaid,
}) {
  const count = applicantList.length;
  const [paymentDetails, setPaymentDetails] = useState(PAYMENT_DETAILS_DEFAULT);
  const setPayF = (e) => setPaymentDetails(d => ({ ...d, [e.target.name]: e.target.value }));
  const category = PAYMENT_METHODS[selectedPaymentMethod]?.category;

  const svcFee     = app?.svcFee     || 0;
  const govFee     = app?.govFee     || 0;
  const isFirstPayment = !hasPaid;
  const protectFee = isFirstPayment ? PROTECT_FEE_FIRST : PROTECT_FEE_REPEAT;
  const expressFee = app?.expressFee  || 0;
  const totalSvc   = svcFee * count;
  const totalGov   = govFee * count;
  const finalSvc   = Math.max(0, totalSvc + protectFee - discountAmount);

  const canUseWallet = walletBalance > 0 && finalSvc >= MIN_SVC_FEE_REDEMPTION;
  const walletDisc   = useWalletOffer && canUseWallet ? Math.min(walletBalance, WELCOME_BONUS) : 0;
  const payableBeforeFee = totalGov + finalSvc + expressFee;

  const convFees = calculateConvenienceFee(payableBeforeFee, selectedPaymentMethod);
  const selectedMethodConfig = PAYMENT_METHODS[selectedPaymentMethod];
  const grandTotal = convFees.grandTotal - walletDisc;

  return (
    <div className="ProfilePayOverlay">
      <div className="ProfilePayModal">
        <div className="ProfilePayModalHeader">
          <div className="ProfilePayModalTitle">
            <div className="ProfilePayModalFlag"><Icons.CreditCard /></div>
            <div>
              <div className="ProfilePayModalCountry">Review &amp; Pay</div>
              <div className="ProfilePayModalType">{count} Applications</div>
            </div>
          </div>
          <button className="ProfilePayModalClose" onClick={onClose}><Icons.Close /></button>
        </div>

        <div className="ProfilePayModalBody">

          {userRole === "agent" && authorisedName && (
            <div className="ProfileAgentAuthSummary">
              <div className="ProfileAgentAuthSummaryLabel"><Icons.Shield /> Authorised Representative</div>
              <div className="ProfileAgentAuthSummaryRow">
                <span>{authorisedName}</span>
                <span className="ProfileAgentAuthSummaryId">{authorisedId}</span>
              </div>
              <div className="ProfileAgentAuthSummaryRow" style={{ marginTop: 6 }}>
                <span style={{ fontSize: 11.5, color: "#6b7280" }}>Phone</span>
                <span className="ProfileAgentAuthSummaryId">{authorisedCountryCode ? `${authorisedCountryCode} ` : ""}{authorisedPhone || "—"}</span>
              </div>
            </div>
          )}

          <div className="ProfilePayFeeSummary">
            <div className="ProfilePfsRow">
              <span>Government Fee ({count}×)</span>
              <span>₹{totalGov.toLocaleString("en-IN")}</span>
            </div>
            <div className="ProfilePfsRow">
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                Service Fee ({count}×)
                <FeeInfoTooltip svcFee={svcFee} />
              </span>
              <span>₹{totalSvc.toLocaleString("en-IN")}</span>
            </div>
            <div className="ProfilePfsRow">
              <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                Visa Protect
                <VisaProtectTooltip side="left" />
              </span>
              <span>₹{protectFee.toLocaleString("en-IN")}</span>
            </div>
            {expressFee > 0 && (
              <div className="ProfilePfsRow">
                <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  &#9889; Express Processing
                </span>
                <span style={{ color: "#2563eb", fontWeight: 600 }}>₹{expressFee.toLocaleString("en-IN")}</span>
              </div>
            )}
            {discountAmount > 0 && (
              <div className="ProfilePfsRow ProfilePfsDiscount">
                <span>Referral Discount</span>
                <span>−₹{discountAmount.toLocaleString("en-IN")}</span>
              </div>
            )}
            {walletDisc > 0 && (
              <div className="ProfilePfsRow ProfilePfsDiscount">
                <span>Welcome Offer</span>
                <span>−₹{walletDisc.toLocaleString("en-IN")}</span>
              </div>
            )}
            {convFees.totalFee > 0 && (
              <>
                <div className="ProfilePfsDivider" />
                <div className="ProfilePfsRow ProfilePfsConv">
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    Convenience Fee
                    <ConvFeeInfoTooltip
                      baseFee={convFees.baseFee}
                      gst={convFees.gst}
                      total={convFees.totalFee}
                    />
                    <span className="ProfilePfsConvTag">
                      {selectedMethodConfig?.shortLabel}
                    </span>
                  </span>
                  <span>₹{convFees.totalFee.toLocaleString("en-IN")}</span>
                </div>
              </>
            )}
            <div className="ProfilePfsDivider" />
            <div className="ProfilePfsRow ProfilePfsTotal">
              <span>Total Payable</span>
              <span>₹{grandTotal.toLocaleString("en-IN")}</span>
            </div>
            {convFees.totalFee > 0 && (
              <div className="ProfilePfsConvNote">
                ₹{convFees.baseFee.toLocaleString("en-IN")} convenience fee + GST (18%) via {selectedMethodConfig?.shortLabel}
              </div>
            )}
            {convFees.totalFee === 0 && (
              <div className="ProfilePfsConvNote ProfilePfsConvNote--free">
                <Icons.Check /> No convenience fee for {selectedMethodConfig?.shortLabel}
              </div>
            )}
          </div>

          <div className="ProfilePaySummarySection">
            <div className="ProfilePaySummaryLabel">Applicants Summary</div>
            <div className="ProfilePaySummaryNames">
              {applicantList.slice(0, 3).map((a, i) => (
                <div key={i} className="ProfilePaySummaryName">
                  <span className="ProfilePsnNum">{i + 1}</span>
                  {a.firstName || "Unnamed"} {a.lastName}
                  <span className="ProfilePsnPassport">{a.passportNumber}</span>
                </div>
              ))}
              {count > 3 && (
                <div style={{ fontSize: 11.5, color: "var(--Profile-text-5)", marginTop: 4 }}>
                  + {count - 3} more
                </div>
              )}
            </div>
          </div>

          <PaymentMethodSelector
            selectedMethod={selectedPaymentMethod}
            onSelect={(key) => { setSelectedPaymentMethod(key); setPaymentDetails(PAYMENT_DETAILS_DEFAULT); }}
            baseAmount={payableBeforeFee}
          />

          {/* ── Payment Details ── */}
          {category && (
            <div className="ProfilePayDetailSection">
              <div className="ProfilePaySummaryLabel" style={{ marginBottom: 10 }}>Payment Details</div>
              {(category === "debit" || category === "credit") && (
                <>
                  <div className="ProfilePfField">
                    <label>Name on Card *</label>
                    <input name="nameOnCard" value={paymentDetails.nameOnCard} onChange={setPayF} placeholder="As printed on card" autoComplete="cc-name" />
                  </div>
                  <div className="ProfilePfField">
                    <label>Card Number *</label>
                    <input name="cardNum" value={paymentDetails.cardNum} onChange={(e) => { const v = e.target.value.replace(/\D/g,"").slice(0,16).replace(/(.{4})/g,"$1 ").trim(); setPaymentDetails(d => ({...d, cardNum: v})); }} placeholder="XXXX XXXX XXXX XXXX" maxLength={19} inputMode="numeric" autoComplete="cc-number" />
                  </div>
                  <div style={{ display:"flex", gap:"0.75rem" }}>
                    <div className="ProfilePfField" style={{ flex:1 }}>
                      <label>Expiry (MM/YY) *</label>
                      <input name="expiry" value={paymentDetails.expiry} onChange={(e) => { const digits = e.target.value.replace(/\D/g,"").slice(0,4); const v = digits.length > 2 ? digits.slice(0,2)+"/"+digits.slice(2) : digits; setPaymentDetails(p => ({...p, expiry: v})); }} placeholder="MM/YY" maxLength={5} autoComplete="cc-exp" />
                    </div>
                    <div className="ProfilePfField" style={{ flex:1 }}>
                      <label>CVV *</label>
                      <input name="cvv" value={paymentDetails.cvv} onChange={setPayF} placeholder="\u2022\u2022\u2022" maxLength={4} type="password" autoComplete="cc-csc" />
                    </div>
                  </div>
                </>
              )}
              {category === "netbanking" && (
                <>
                  <div className="ProfilePfField">
                    <label>Select Bank *</label>
                    <select name="bankName" value={paymentDetails.bankName} onChange={setPayF}>
                      <option value="">— Select your bank —</option>
                      {BANKS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="ProfilePfField">
                    <label>Account Number *</label>
                    <input name="accountNum" value={paymentDetails.accountNum} onChange={setPayF} placeholder="Your bank account number" inputMode="numeric" />
                  </div>
                  <div className="ProfilePfField">
                    <label>IFSC Code *</label>
                    <input name="ifsc" value={paymentDetails.ifsc} onChange={(e) => setPaymentDetails(d => ({...d, ifsc: e.target.value.toUpperCase().slice(0,11)}))} placeholder="e.g. SBIN0001234" maxLength={11} />
                  </div>
                </>
              )}
              {category === "upi" && (
                <div className="ProfilePfField">
                  <label>UPI ID *</label>
                  <input
                    name="upiId"
                    value={paymentDetails.upiId}
                    onChange={setPayF}
                    placeholder="example@ybl"
                    style={{ textTransform: 'lowercase' }}
                  />
                  <div style={{ fontSize:"0.75rem", color:"var(--Profile-text-4)", marginTop:4 }}>
                    Enter your UPI ID (e.g., name@ybl, name@okaxis, name@paytm)
                  </div>
                </div>
              )}
              <div style={{ fontSize:"0.78rem", color:"var(--Profile-text-5)", marginTop:8, display:"flex", alignItems:"center", gap:4 }}>
                🔒 Your payment details are encrypted and secure
              </div>
            </div>
          )}

          <div className="ProfilePayWalletOfferSection">
            <div className="ProfilePaySummaryLabel">
              <Icons.Gift /> Welcome Offer (Non-Redeemable Balance)
            </div>
            <div
              onClick={() => walletBalance > 0 && setUseWalletOffer(!useWalletOffer)}
              className={`ProfileWalletOfferToggle ${useWalletOffer ? 'active' : ''} ${walletBalance === 0 ? 'disabled' : ''}`}
              role="button"
              tabIndex={walletBalance === 0 ? -1 : 0}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && walletBalance > 0) { setUseWalletOffer(!useWalletOffer); } }}
            >
              <div className="ProfileWalletOfferToggleIndicator">
                <div className={`ProfileWalletOfferToggleCircle ${useWalletOffer ? 'active' : ''}`} />
              </div>
              <span className="ProfileWalletOfferToggleLabel">
                {useWalletOffer ? '✓ ' : ''}Apply ₹{Math.min(walletBalance, WELCOME_BONUS)} welcome discount
                <span className="ProfileWalletOfferBadge">
                  {walletBalance === 0
                    ? 'Insufficient Balance'
                    : finalSvc >= MIN_SVC_FEE_REDEMPTION
                    ? `Available: ₹${walletBalance}`
                    : `Min ₹${MIN_SVC_FEE_REDEMPTION} fee required`
                  }
                </span>
              </span>
            </div>
          </div>

          <div className="ProfilePayReferralSection">
            <div className="ProfilePaySummaryLabel">Referral Code (Optional)</div>
            {referralApplied ? (
              <div className="ProfileReferralApplied">
                <Icons.Check /> Code applied — ₹{discountAmount} off service fee
              </div>
            ) : (
              <div className="ProfileReferralInputRow">
                <input
                  className="ProfileReferralInput"
                  value={referralInput}
                  onChange={(e) => setReferralInput(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                />
                <button className="ProfileBtn ProfileBtnOutline ProfileBtnSm" onClick={applyReferralCode}>Apply</button>
              </div>
            )}
            {referralError && <div className="ProfileReferralError">{referralError}</div>}
          </div>
        </div>

        <div className="ProfilePayModalFooter">
          <button className="ProfileBtn ProfileBtnOutline" onClick={onBack}> Back</button>
          <button
            className="ProfileBtnPay"once the
            onClick={() => {
              const err = validatePaymentDetails(category, paymentDetails);
              if (err) { alert(err); return; }
              handlePayNow(selectedPaymentMethod, paymentDetails);
            }}
            disabled={payProcessing}
          >
            {payProcessing
              ? <><span className="ProfileSpinner" /> Processing…</>
              : <><Icons.CreditCard /> Pay ₹{grandTotal.toLocaleString("en-IN")}</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PaymentModalStep4 ────────────────────────────────────────────────────────
function PaymentModalStep4({
  app, applicantList, user,
  onNext, onBack, onClose,
  userRole,
}) {
  const [consentChecked, setConsentChecked] = useState(false);
  const count = applicantList?.length || 0;

  return (
    <div className="ProfilePayOverlay">
      <div className="ProfilePayModal">

        {/* ── Header ── */}
        <div className="ProfileConsentHeader">
          <div className="ProfileConsentHeaderLeft">
            <div className="ProfileConsentHeaderIcon">
              <Icons.Shield />
            </div>
            <div>
              <div className="ProfileConsentTitle">Terms & Consent</div>
              <div className="ProfileConsentSubtitle">
                {count} Application{count !== 1 ? "s" : ""} · {app?.countryName}
              </div>
            </div>
          </div>
          <button className="ProfilePayModalClose" onClick={onClose}>
            <Icons.Close />
          </button>
        </div>

        {/* ── Scroll Hint ── */}
        <div className="ProfileConsentScrollHint">
          <Icons.Info />
          Please read all terms carefully before proceeding
        </div>

        {/* ── Body ── */}
        <div className="ProfileConsentBody">

          <p className="ProfileConsentIntro">
            By proceeding with this visa application and making payment, you confirm
            and acknowledge the following terms and conditions on behalf of all
            {count > 1 ? ` ${count} applicants` : " the applicant"}.
          </p>

          {CONSENT_CLAUSES.map((clause) => (
            <div key={clause.id} className="ProfileConsentClause">
              <div className="ProfileConsentClauseNum">{clause.id}</div>
              <div className="ProfileConsentClauseContent">
                <span className="ProfileConsentClauseTitle">{clause.title}</span>
                <span className="ProfileConsentClauseBody">{clause.body}</span>
              </div>
            </div>
          ))}

          {/* ── Declaration + Checkbox ── */}
          <div className="ProfileConsentDeclarationBox">
            <div className="ProfileConsentDeclarationLabel">
              <Icons.Check /> Declaration
            </div>
            {/* <p className="ProfileConsentDeclarationText">
              {CONSENT_DECLARATION_TEXT ||
                "I hereby declare that all information provided is true and correct to the best of my knowledge. I understand that providing false information may result in rejection of the visa application."}
            </p> */}
            <label className="ProfileConsentCheckRow">
              <span className="ProfileConsentCheckboxWrapper">
                <input
                  type="checkbox"
                  className="ProfileConsentCheckbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                />
                <span className="ProfileConsentCheckmark"></span>
              </span>
              <span className="ProfileConsentCheckLabel">
                I have read and agree to all the terms and conditions above
              </span>
            </label>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="ProfilePayModalFooter">
          <button className="ProfileBtn ProfileBtnOutline" onClick={onBack}>
             Back
          </button>
          <button
            className={`ProfileBtnPay ${!consentChecked ? "ProfileBtnPay--disabled" : ""}`}
            onClick={onNext}
            disabled={!consentChecked}
          >
            Continue 
          </button>
        </div>

      </div>
    </div>
  );
}
export { CONSENT_CLAUSES };
